import { Box, Link, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import PropTypes from 'prop-types'
import React from 'react'

import { formatDate } from '../../../../utils/functions'
import Html from '../../../Html/Html'

const ObjectContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showOnlyDesktop',
})(({ theme, showOnlyDesktop }) => ({
  display: showOnlyDesktop ? 'none' : 'block',
  padding: `0 ${theme.spacing(2)}`,
  [theme.breakpoints.up('md')]: {
    position: 'relative',
    marginTop: theme.spacing(4),
    marginBottom: theme.spacing(4),
    display: 'block',
  },
  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
}))

const TextContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
}))

const Row = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'row',
  },
}))

const Logo = styled(GatsbyImage)(({ theme }) => ({
  marginRight: theme.spacing(1.25),
  [theme.breakpoints.down('sm')]: {
    flex: 1,
    maxWidth: 200,
  },
}))

const TagsRoot = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  rowGap: theme.spacing(0.625),
}))

const RatingTagTitle = styled('span')(({ theme }) => ({
  fontWeight: 'bold',
}))

const DescriptionContainer = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(3),
}))

const FundNameAsOfDate = styled('span')(({ theme }) => ({
  lineHeight: '24px',
  fontWeight: theme.typography.fontWeightRegular,
  display: 'block',
  margin: theme.spacing(0.625, 0, 1.25),
}))

const linkStyles = (theme) => ({
  color: theme.palette.text.primary,
  fontWeight: 500,
})
const morningStarTextStyle = { fontSize: '16px', lineHeight: '32px' }
const typographyStyle = { fontWeight: 500 }

const MorningstarAnalystRating = ({
  morningstar_rating,
  showOnlyDesktop = false,
}) => {
  if (!morningstar_rating || Object.keys(morningstar_rating).length === 0) {
    return null
  }

  const { analyst_driven_pct, data_coverage_pct } = morningstar_rating

  return (
    <ObjectContainer showOnlyDesktop={showOnlyDesktop}>
      <TextContainer>
        <Typography variant="h3" sx={typographyStyle}>
          {morningstar_rating.header}
        </Typography>
        <Row>
          {morningstar_rating.rating_image?.localAsset ? (
            <Logo
              image={getImage(morningstar_rating.rating_image.localAsset)}
              alt={morningstar_rating.rating_image.description || ''}
              objectFit="contain"
            />
          ) : (
            <img alt={morningstar_rating.rating || ''} />
          )}
          <TagsRoot>
            {analyst_driven_pct && (
              <Box>
                <RatingTagTitle>Analyst-Driven %</RatingTagTitle>
                <br />
                <span>{analyst_driven_pct}</span>
              </Box>
            )}
            {data_coverage_pct && (
              <Box>
                <RatingTagTitle>Data Coverage %</RatingTagTitle>
                <br />
                <span>{data_coverage_pct}</span>
              </Box>
            )}
          </TagsRoot>
        </Row>
        <DescriptionContainer>
          {morningstar_rating.rating_date && (
            <FundNameAsOfDate>
              As of {formatDate(morningstar_rating.rating_date)}
            </FundNameAsOfDate>
          )}
          <Typography variant="body" sx={morningStarTextStyle}>
            <Html content={morningstar_rating.description} />
          </Typography>
          <Typography>
            <Link
              href={morningstar_rating.description_link}
              target="_blank"
              rel="noreferrer"
              underline="none"
              sx={linkStyles}
            >
              Source: Morningstar
            </Link>
          </Typography>
        </DescriptionContainer>
      </TextContainer>
    </ObjectContainer>
  )
}

MorningstarAnalystRating.propTypes = {
  morningstar_rating: PropTypes.shape({
    header: PropTypes.string,
    rating: PropTypes.string,
    rating_image: PropTypes.shape({
      localAsset: PropTypes.object,
      description: PropTypes.string,
    }),
    rating_date: PropTypes.string,
    description: PropTypes.string,
    description_link: PropTypes.string,
    analyst_driven_pct: PropTypes.string,
    data_coverage_pct: PropTypes.string,
  }),
  showOnlyDesktop: PropTypes.bool,
}

export default MorningstarAnalystRating